import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Clock, Check, Package, Truck, Store, XCircle, Phone, MapPin, RefreshCw, Printer, Bell, Trash2, Search, CheckCheck, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import OrderPrintView from '@/components/OrderPrintView';
import { useOrderNotifications } from '@/hooks/useOrderNotifications';
import { OrderEditItemsDialog } from '@/components/OrderEditItemsDialog';

const statusConfig = {
  new: { label: 'Novo', icon: Clock, color: 'status-new', bg: 'bg-blue-500/10' },
  confirmed: { label: 'Confirmado', icon: Check, color: 'status-confirmed', bg: 'bg-yellow-500/10' },
  preparing: { label: 'Preparando', icon: Package, color: 'status-preparing', bg: 'bg-purple-500/10' },
  out_for_delivery: { label: 'Em Entrega', icon: Truck, color: 'status-delivery', bg: 'bg-cyan-500/10' },
  ready_for_pickup: { label: 'Pronto Retirada', icon: Store, color: 'status-delivery', bg: 'bg-cyan-500/10' },
  completed: { label: 'Concluído', icon: Check, color: 'status-completed', bg: 'bg-green-500/10' },
  cancelled: { label: 'Cancelado', icon: XCircle, color: 'status-cancelled', bg: 'bg-red-500/10' }
};

const statusFlow = ['new', 'confirmed', 'preparing'];
const HISTORY_PAGE = 50;

const getNextStatus = (currentStatus, deliveryType) => {
  if (currentStatus === 'new') return 'confirmed';
  if (currentStatus === 'confirmed') return 'preparing';
  if (currentStatus === 'preparing') {
    return deliveryType === 'delivery' ? 'out_for_delivery' : 'ready_for_pickup';
  }
  if (currentStatus === 'out_for_delivery' || currentStatus === 'ready_for_pickup') {
    return 'completed';
  }
  return null;
};

const OrderCard = memo(({ order, showActions = true, onSelect, onAdvance, onPrint, onCancel }) => {
  const status = statusConfig[order.status] || statusConfig.new;
  const StatusIcon = status.icon || Clock;
  const isDelivery = order.delivery_type === 'delivery';
  const nextStatus = getNextStatus(order.status, order.delivery_type);

  return (
    <Card
      className={`card-adegga cursor-pointer hover:border-[#ff6b00]/50 transition-colors ${status.bg}`}
      onClick={() => onSelect(order)}
      data-testid={`order-card-${order.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-lg">#{order.order_number}</span>
              <Badge className={status.color}>{status.label}</Badge>
            </div>
            <p className="text-white/60 text-sm">{order.customer_name}</p>
          </div>
          <span className="text-[#ff6b00] font-bold">R$ {order.total.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-white/60 mb-3">
          {isDelivery ? <Truck className="w-4 h-4" /> : <Store className="w-4 h-4" />}
          <span>{isDelivery ? 'Entrega' : 'Retirada'}</span>
          {isDelivery && order.delivery_fee > 0 && (
            <>
              <span className="text-white/30">•</span>
              <span className="text-[#ff6b00]">+R$ {order.delivery_fee.toFixed(2)} taxa</span>
            </>
          )}
          <span className="text-white/30">•</span>
          <span>{new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="text-sm text-white/50 mb-3">
          {order.items.slice(0, 2).map((item, i) => (
            <span key={i}>{item.quantity}x {item.product_name}{i < Math.min(order.items.length - 1, 1) ? ', ' : ''}</span>
          ))}
          {order.items.length > 2 && <span> +{order.items.length - 2} itens</span>}
        </div>
        {showActions && nextStatus && (
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              className="flex-1 bg-[#ff6b00] hover:bg-[#ff8533]"
              onClick={(e) => { e.stopPropagation(); onAdvance(order.id, nextStatus); }}
              data-testid={`order-advance-${order.id}`}
            >
              <StatusIcon className="w-4 h-4 mr-1" />
              {statusConfig[nextStatus]?.label}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20"
              onClick={(e) => { e.stopPropagation(); onPrint(order); }}
              data-testid={`order-print-${order.id}`}
            >
              <Printer className="w-4 h-4" />
            </Button>
            {order.status === 'new' && (
              <Button
                size="sm"
                variant="secondary"
                className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                onClick={(e) => { e.stopPropagation(); onCancel(order); }}
              >
                <XCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const searchRef = useRef('');
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [wsConnected, setWsConnected] = useState(false);

  // Check if store is open based on schedule
  const isStoreOpen = useCallback(() => {
    if (!store?.schedule) return true;
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayIndex = now.getDay();
    const yesterdayIndex = (todayIndex + 6) % 7;
    
    const today = store.schedule[days[todayIndex]];
    const yesterday = store.schedule[days[yesterdayIndex]];
    
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    // Check if store is 24h today
    if (today?.active !== false && today?.is_24h) {
      return true;
    }
    
    // Check if yesterday's overnight shift is still active
    if (yesterday?.active !== false && yesterday?.overnight && !yesterday?.is_24h) {
      const closeTime = parseInt((yesterday.close || '22:00').replace(':', ''));
      if (closeTime < 1200 && currentTime < closeTime) {
        return true;
      }
    }
    
    // Normal check for today
    if (!today?.active) return false;
    
    const openTime = parseInt((today.open || '08:00').replace(':', ''));
    const closeTime = parseInt((today.close || '22:00').replace(':', ''));
    
    // If overnight, we only need to check if we're after open time
    if (today.overnight) {
      return currentTime >= openTime;
    }
    
    // Normal case: between open and close
    return currentTime >= openTime && currentTime <= closeTime;
  }, [store]);

  // Handle new order notification with auto-print support
  const handleNewOrder = useCallback((data) => {
    playNotificationSound();
    fetchOrders();
  }, []);

  // Get the printOrder function for manual printing
  // WebSocket connection is now handled globally in DashboardLayout
  const { printOrder: triggerPrint } = useOrderNotifications(null, null, false);

  useEffect(() => {
    fetchStore();
    const interval = setInterval(() => {
      if (!document.hidden) fetchOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    searchRef.current = debouncedSearch;
    fetchOrders();
    if (activeTab === 'completed') fetchHistory(true);
  }, [debouncedSearch]);

  useEffect(() => {
    if (activeTab === 'completed') fetchHistory(true);
  }, [activeTab]);

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 200);
    } catch (e) {
      console.error('Could not play notification sound:', e);
    }
  };

  const fetchOrders = async () => {
    try {
      const params = { scope: 'active', limit: 500 };
      if (searchRef.current) params.search = searchRef.current;
      const res = await axios.get(`${API}/orders`, { params });
      setOrders(res.data);
    } catch (e) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (reset = false) => {
    setHistoryLoading(true);
    try {
      const skip = reset ? 0 : history.length;
      const params = { scope: 'history', limit: HISTORY_PAGE, skip };
      if (searchRef.current) params.search = searchRef.current;
      const res = await axios.get(`${API}/orders`, { params });
      setHistory(prev => reset ? res.data : [...prev, ...res.data]);
      setHistoryHasMore(res.data.length === HISTORY_PAGE);
    } catch (e) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleBulkComplete = async () => {
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-complete`);
      toast.success(`${res.data.completed} pedido(s) marcado(s) como concluído(s)`);
      setShowBulkDialog(false);
      fetchOrders();
      if (activeTab === 'completed') fetchHistory(true);
    } catch (e) {
      toast.error('Erro ao concluir pedidos');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleItemsSaved = (updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
    setSelectedOrder(updated);
    setEditOrder(null);
  };

  const fetchStore = async () => {
    try {
      const res = await axios.get(`${API}/stores/me`);
      setStore(res.data);
    } catch (e) {
      console.error('Error fetching store:', e);
    }
  };

  const handlePrint = useCallback((order) => {
    setPrintOrder(order);
    setShowPrintDialog(true);
  }, []);

  const handleCancelClick = useCallback((order) => {
    setSelectedOrder(order);
    setShowCancelDialog(true);
  }, []);

  const handleUpdateStatus = async (orderId, newStatus, reason = null) => {
    try {
      const data = { status: newStatus };
      if (reason) data.cancel_reason = reason;
      await axios.put(`${API}/orders/${orderId}/status`, data);
      toast.success('Status atualizado');
      fetchOrders();
      if (activeTab === 'completed') fetchHistory(true);
      setSelectedOrder(null);
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    try {
      await axios.delete(`${API}/orders/${selectedOrder.id}`);
      toast.success('Pedido excluído');
      fetchOrders();
      setSelectedOrder(null);
      setShowDeleteDialog(false);
    } catch (e) {
      toast.error('Erro ao excluir pedido');
    }
  };

  const activeOrders = orders;
  const completedOrders = history;

  const groupedByStatus = statusFlow.reduce((acc, status) => {
    acc[status] = activeOrders.filter(o => o.status === status);
    return acc;
  }, {});
  groupedByStatus['in_progress'] = activeOrders.filter(o => 
    ['out_for_delivery', 'ready_for_pickup'].includes(o.status)
  );

  const cardProps = { onSelect: setSelectedOrder, onAdvance: handleUpdateStatus, onPrint: handlePrint, onCancel: handleCancelClick };

  if (loading) {
    return (
      <DashboardLayout title="Pedidos">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Pedidos">
      <div className="pb-20 lg:pb-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[#0f0f15]">
              <TabsTrigger value="active" className="data-[state=active]:bg-[#ff6b00]">
                Ativos ({activeOrders.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:bg-[#ff6b00]">
                Histórico{completedOrders.length ? ` (${completedOrders.length}${historyHasMore ? '+' : ''})` : ''}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Buscar cliente, telefone ou nº do pedido..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-[#0f0f15] border-white/10 text-white"
              data-testid="orders-search-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => setShowBulkDialog(true)}
              disabled={!debouncedSearch && activeOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="bulk-complete-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Concluir todos{!debouncedSearch ? ` (${activeOrders.length})` : ''}
            </Button>
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${isStoreOpen() ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              <Store className="w-3 h-3" />
              {isStoreOpen() ? 'Aberto' : 'Fechado'}
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => window.open('/app/impressao', '_blank')}
              className="border-[#ff6b00]/50 text-[#ff6b00] hover:bg-[#ff6b00]/10"
            >
              <Printer className="w-4 h-4 mr-2" />
              Estação de Impressão
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { fetchOrders(); if (activeTab === 'completed') fetchHistory(true); }} className="text-white/60" data-testid="orders-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>

        {activeTab === 'active' ? (
          <>
            {/* Kanban View - Desktop */}
            <div className="hidden lg:grid lg:grid-cols-4 gap-4">
              {['new', 'confirmed', 'preparing', 'in_progress'].map(status => (
                <div key={status} className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className={status === 'in_progress' ? 'status-delivery' : statusConfig[status]?.color}>
                      {status === 'in_progress' ? 'Em Andamento' : statusConfig[status]?.label}
                    </Badge>
                    <span className="text-white/40 text-sm">({groupedByStatus[status]?.length || 0})</span>
                  </div>
                  <ScrollArea className="h-[calc(100vh-250px)]">
                    <div className="space-y-3 pr-2">
                      {(groupedByStatus[status] || []).map(order => (
                        <OrderCard key={order.id} order={order} {...cardProps} />
                      ))}
                      {(groupedByStatus[status] || []).length === 0 && (
                        <div className="text-center py-8 text-white/30">
                          Nenhum pedido
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              ))}
            </div>

            {/* List View - Mobile */}
            <div className="lg:hidden space-y-3">
              {activeOrders.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  Nenhum pedido ativo
                </div>
              ) : (
                activeOrders.map(order => (
                  <OrderCard key={order.id} order={order} {...cardProps} />
                ))
              )}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {completedOrders.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                Nenhum pedido no histórico
              </div>
            ) : (
              completedOrders.map(order => (
                <OrderCard key={order.id} order={order} showActions={false} {...cardProps} />
              ))
            )}
            {historyHasMore && (
              <Button
                variant="secondary"
                className="w-full bg-white/10 hover:bg-white/20"
                onClick={() => fetchHistory(false)}
                disabled={historyLoading}
                data-testid="history-load-more-btn"
              >
                {historyLoading ? 'Carregando...' : 'Carregar mais'}
              </Button>
            )}
          </div>
        )}

        {/* Order Detail Modal */}
        <Dialog open={!!selectedOrder && !showCancelDialog && !editOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="bg-[#13131f] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            {selectedOrder && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    Pedido #{selectedOrder.order_number}
                    <Badge className={statusConfig[selectedOrder.status]?.color}>
                      {statusConfig[selectedOrder.status]?.label}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Customer Info */}
                  <div className="bg-[#0f0f15] p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2 text-white">
                      <span className="font-medium">{selectedOrder.customer_name}</span>
                    </div>
                    <a 
                      href={`tel:${selectedOrder.customer_phone}`}
                      className="flex items-center gap-2 text-[#ff6b00]"
                    >
                      <Phone className="w-4 h-4" />
                      {selectedOrder.customer_phone}
                    </a>
                    {selectedOrder.delivery_type === 'delivery' && selectedOrder.address && (
                      <div className="flex items-start gap-2 text-white/60">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <div>
                          <p>{selectedOrder.address.street}, {selectedOrder.address.number}</p>
                          {selectedOrder.address.complement && <p>{selectedOrder.address.complement}</p>}
                          <p>{selectedOrder.address.neighborhood} - {selectedOrder.address.city}</p>
                          {selectedOrder.address.reference && (
                            <p className="text-white/40 italic">Ref: {selectedOrder.address.reference}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Items */}
                  <div className="bg-[#0f0f15] p-4 rounded-lg space-y-3">
                    <h4 className="font-medium text-white">Itens</h4>
                    {selectedOrder.items.map((item, i) => (
                      <div key={i} className="flex justify-between">
                        <div>
                          <span className="text-white">{item.quantity}x {item.product_name}</span>
                          {item.variant_name && (
                            <span className="text-white/60 text-sm"> ({item.variant_name})</span>
                          )}
                          {item.addons?.length > 0 && (
                            <p className="text-xs text-white/40">
                              {item.addons.map(a => `+ ${a.quantity}x ${a.name}`).join(', ')}
                            </p>
                          )}
                          {item.notes && (
                            <p className="text-xs text-yellow-400">Obs: {item.notes}</p>
                          )}
                        </div>
                        <span className="text-white">R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="bg-[#0f0f15] p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-white/60">
                      <span>Subtotal</span>
                      <span>R$ {selectedOrder.subtotal.toFixed(2)}</span>
                    </div>
                    {selectedOrder.delivery_fee > 0 && (
                      <div className="flex justify-between text-white/60">
                        <span>Taxa de entrega</span>
                        <span>R$ {selectedOrder.delivery_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.discount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Desconto</span>
                        <span>-R$ {selectedOrder.discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t border-white/10 pt-2">
                      <span className="text-white">Total</span>
                      <span className="text-[#ff6b00]">R$ {selectedOrder.total.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-white/60">
                      Pagamento: {selectedOrder.payment_method === 'pix' ? 'Pix' : selectedOrder.payment_method === 'cash' ? 'Dinheiro' : 'Cartão'}
                      {selectedOrder.change_for && ` (Troco para R$ ${selectedOrder.change_for})`}
                    </p>
                  </div>

                  {selectedOrder.notes && (
                    <div className="bg-yellow-500/10 p-4 rounded-lg">
                      <p className="text-sm text-yellow-400">Observações: {selectedOrder.notes}</p>
                    </div>
                  )}
                </div>

                <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                  <Button
                    variant="secondary"
                    className="bg-white/10"
                    onClick={() => handlePrint(selectedOrder)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimir
                  </Button>
                  {!['completed', 'cancelled'].includes(selectedOrder.status) && (
                    <Button
                      variant="secondary"
                      className="bg-white/10"
                      onClick={() => setEditOrder(selectedOrder)}
                      data-testid="edit-order-items-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Editar itens
                    </Button>
                  )}
                  {getNextStatus(selectedOrder.status, selectedOrder.delivery_type) && (
                    <Button
                      className="flex-1 bg-[#ff6b00] hover:bg-[#ff8533]"
                      onClick={() => handleUpdateStatus(
                        selectedOrder.id,
                        getNextStatus(selectedOrder.status, selectedOrder.delivery_type)
                      )}
                    >
                      {statusConfig[getNextStatus(selectedOrder.status, selectedOrder.delivery_type)]?.label}
                    </Button>
                  )}
                  {!['completed', 'cancelled'].includes(selectedOrder.status) && (
                    <Button
                      variant="secondary"
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      Cancelar
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    className="bg-red-600/20 hover:bg-red-600/30 text-red-400"
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid="delete-order-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Cancelar Pedido</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-white/60">Informe o motivo do cancelamento:</p>
              <Input
                placeholder="Motivo do cancelamento"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCancelDialog(false)}>
                Voltar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleUpdateStatus(selectedOrder?.id, 'cancelled', cancelReason)}
              >
                Confirmar Cancelamento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Order Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Excluir Pedido</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white/60">
                Tem certeza que deseja excluir o pedido <strong className="text-white">#{selectedOrder?.order_number}</strong>?
              </p>
              <p className="text-sm text-yellow-400 mt-2">Esta ação não pode ser desfeita. O pedido será removido permanentemente.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
                Voltar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDeleteOrder}
                data-testid="confirm-delete-order"
              >
                Excluir Pedido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {editOrder && (
          <OrderEditItemsDialog order={editOrder} onClose={() => setEditOrder(null)} onSaved={handleItemsSaved} />
        )}

        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Concluir todos os pedidos abertos</DialogTitle>
            </DialogHeader>
            <p className="text-white/60 py-2">
              Todos os pedidos abertos da loja{!debouncedSearch && <> (<strong className="text-white">{activeOrders.length}</strong>)</>} (novos, confirmados, preparando, em entrega e prontos para retirada) serão marcados como <strong className="text-green-400">Concluído</strong> e lançados no financeiro.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowBulkDialog(false)}>Voltar</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleBulkComplete} disabled={bulkLoading} data-testid="confirm-bulk-complete-btn">
                {bulkLoading ? 'Concluindo...' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Print Dialog */}
        {showPrintDialog && printOrder && (
          <OrderPrintView
            order={printOrder}
            store={store}
            onClose={() => {
              setShowPrintDialog(false);
              setPrintOrder(null);
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Orders;
